import cn from "classnames"
import React from "react"
import ScrollList, { type ScrollListProps } from "./scroll_list"
import { type BlockProps } from "."
import { graphql, getFragment, type FragmentType } from "@/api"
import { ArrowRight } from "@/skylticons"

export const LinkCarouselFragment = graphql(`
  fragment FrontpagesLinkCarousel on FrontpagesLinkCarousel {
    id
    heading

    items {
      image {
        data
      }

      ...FrontpageLinkItem_Example
    }
  }
`)

export const ItemFragment = graphql(`
  fragment FrontpageLinkItem_Example on FrontpagesLinkCarouselItem {
    id
    title
    url

    image {
      data
      width
      height
      pngDefault
      pngTwox
      webpDefault
      webpTwox
    }
  }
`)

interface LinkItemProps {
  item: FragmentType<typeof ItemFragment>
  noImage?: boolean
  gtmTag?: string
}

const LinkItem: React.FC<LinkItemProps> = ({ item: itemFragment, gtmTag, noImage }) => {
  const item = getFragment(ItemFragment, itemFragment)

  const width = "w-40 lg:w-[182px] xl:w-[192px] 2xl:w-[198px]"

  if (noImage || !item.image) {
    return (
      <div>
        <a href={item.url} className="group" data-gtm={gtmTag}>
          <div
            className={cn(
              "flex h-32 items-center rounded-xl border border-transparent bg-white-5 px-2 py-3 transition-colors group-hover:border-ink-50",
              width
            )}
          >
            <h3 className="heading-five mb-0 text-center group-hover:underline">{item.title}</h3>
          </div>
        </a>
      </div>
    )
  }

  return (
    <div>
      <a href={item.url} data-gtm={gtmTag} className="text-ink-100 hover:underline">
        <div>
          <div className={cn("mb-2 h-40 overflow-hidden rounded-xl bg-white-5 lg:h-44 xl:h-48", width)}>
            <picture>
              <source srcSet={`${item.image.webpDefault}, ${item.image.webpTwox} 2x`} type="image/webp" />
              <img
                srcSet={`${item.image.pngDefault}, ${item.image.pngTwox} 2x`}
                width={item.image.width || 0}
                height={item.image.height || 0}
                className="h-full w-full object-cover"
                loading="lazy"
              />
            </picture>
          </div>
          <h3 className="heading-five mb-0 flex w-40 gap-1 lg:w-44 xl:w-48">
            <div>{item.title}</div>
            <ArrowRight className="h-6 w-6 shrink-0" />
          </h3>
        </div>
      </a>
    </div>
  )
}

interface LinkListProps extends ScrollListProps {
  items: FragmentType<typeof ItemFragment>[]
  noImages?: boolean
  gtmTag?: string
}

const LinkList: React.FC<LinkListProps> = ({ items, noImages, gtmTag, ...listProps }) => {
  return (
    <ScrollList {...listProps}>
      {items.map(item => (
        <LinkItem key={getFragment(ItemFragment, item).id} item={item} noImage={noImages} />
      ))}
    </ScrollList>
  )
}

const LinkCarouselBlock: React.FC<BlockProps<typeof LinkCarouselFragment> & { divider?: boolean }> = ({
  blockable,
  index,
  divider,
}) => {
  const block = getFragment(LinkCarouselFragment, blockable)
  const items = block.items
  const noImages = items.some(i => !i.image)

  return (
    <>
      <div className="container pt-4">
        <h3 className="heading-three mb-6" data-gtm={`fp-carousel-${index}`}>
          {block.heading}
        </h3>

        <div className="hidden pb-4 lg:block">
          <LinkList
            buttonClassName={noImages ? "top-1/2" : "top-20 lg:top-[5.5rem] xl:top-24"}
            buttonsOutside
            items={items}
            noImages={noImages}
            gtmTag="fp-most-popular-item"
            moreButtonGtmTag="fp-most-popular-more"
          />
        </div>
      </div>

      {divider && (
        <div className="container hidden lg:block">
          <div className="-mb-8 border-b border-white-10 pb-8"></div>
        </div>
      )}

      <div className="lg:hidden">
        <LinkList
          items={items}
          noImages={noImages}
          buttonClassName={noImages ? "top-1/2" : "top-20 lg:top-[5.5rem] xl:top-24"}
          buttonsOutside
          gap={12}
          gtmTag="fp-most-popular-item"
          moreButtonGtmTag="fp-most-popular-more"
        />
      </div>
    </>
  )
}

export default LinkCarouselBlock
